import Icon from "@components/ui/icon";
import Tooltip from "@components/ui/tooltip";
import { useCallback, useRef } from "react";
import { toast } from "react-toastify";
import uuid from "react-uuid";
import { MAX_REFERENCE_IMAGES, MAX_REFERENCE_IMAGE_SIZE } from "../constants";
import { useUploadImage } from "../hooks";
import { useAiImageStoreShallow } from "../store";

export const AiImageUploadReference = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { handleUploadImage } = useUploadImage();
  const {
    setReferenceImages,
    removeReferenceImage,
    updateReferenceImage,
    referenceImages,
  } = useAiImageStoreShallow((s) => ({
    setReferenceImages: s.setReferenceImages,
    removeReferenceImage: s.removeReferenceImage,
    updateReferenceImage: s.updateReferenceImage,
    referenceImages: s.referenceImages,
  }));

  const handleUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      const file = files?.[0];
      const id = uuid();
      try {
        if (file) {
          if (file.size > MAX_REFERENCE_IMAGE_SIZE) {
            toast.error("File size exceeds 10MB limit");
            if (inputRef.current) inputRef.current.value = ""; // clear input value
            return;
          }
          const name = file.name.split(".").slice(0, -1).join(".");
          const extension = file.name.split(".").slice(-1)[0] || "";
          setReferenceImages({
            name,
            url: "",
            extension,
            loading: true,
            id,
          });
          const url = await handleUploadImage({
            fileType: file.type,
            blob: file,
            id,
          });
          if (url) updateReferenceImage(id, { url, loading: false });
          if (inputRef.current) inputRef.current.value = ""; // clear input value
        }
      } catch (error) {
        console.error(error);
        removeReferenceImage(id);
        toast.error("Failed to upload image, try again");
      }
    },
    [
      handleUploadImage,
      setReferenceImages,
      updateReferenceImage,
      removeReferenceImage,
    ],
  );
  const isMaxReached = referenceImages.length >= MAX_REFERENCE_IMAGES;

  return (
    <div>
      <Tooltip>
        <Tooltip.Trigger asChild>
          <button
            aria-label="Add reference image"
            type="button"
            className="tw-flex tw-items-center tw-size-6 tw-absolute tw-top-4 tw-left-4 tw-z-1 tw-justify-center tw-rounded-full tw-bg-blue-50 hover:tw-bg-blue-100 tw-transition-colors tw-duration-200 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed disabled:hover:tw-bg-blue-50"
            onClick={() => inputRef.current?.click()}
            disabled={isMaxReached}
          >
            <Icon icon="ri-add-line" className="tw-text-blue-800" />
          </button>
        </Tooltip.Trigger>
        {isMaxReached && (
          <Tooltip.Content>
            <Tooltip.Text>Maximum of 4 reference images allowed</Tooltip.Text>
          </Tooltip.Content>
        )}
      </Tooltip>
      <input
        type="file"
        className="tw-hidden"
        ref={inputRef}
        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml,image/x-icon"
        multiple={false}
        onChange={handleUpload}
      />
    </div>
  );
};
